<template>
  <v-container>
    <v-row>
      <v-col>
        <exo-input-name :root="_self" />
      </v-col>
      <v-col v-if="this.spaceObject == null && this.scenario == null">
        <exo-input-ref-notparametric
          :root="_self"
          name="spaceObject"
          type="spaceObject"
          componentRoute="/spaceobject"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-expansion-panels accordion multiple v-model="expandedPanels">
        <v-expansion-panel
          v-for="(burn, index) in inputs.customBurnDefinitions"
          :key="burn.id"
          class="outlined-panel"
        >
          <v-expansion-panel-header>
            <v-row class="d-flex justify-center align-center">
              <div>Burn n°{{ index + 1 }}</div>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    v-on="on"
                    color="red"
                    @click.stop="deleteBurn(index, index)"
                    ><v-icon>delete</v-icon></v-btn
                  >
                </template>
                <span> Delete this burn</span>
              </v-tooltip>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <CustomBurnForm
              :ref="burn.id"
              :customBurn="burn"
              v-on:updateCustomBurn="updateBurn(index, $event)"
              v-on:updateValid="updateBurnValid(burn.id, $event)"
              :spaceObject="inputs.spaceObject"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-row>
    <v-row class="d-flex justify-center align-center mt-2">
      <v-btn color="primary" @click="createNewBurn()"
        >Add new burn</v-btn
      ></v-row
    >
  </v-container>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import dataTools from '../../tools/data.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import numericalMixin from '../../mixins/numericalForms.js'
import CustomBurnForm from './CustomBurnForm'
import { v4 as uuid } from 'uuid'

export default {
  name: 'CustomEvasiveScenario',

  components: { CustomBurnForm },

  mixins: [formMixin, numericalMixin],

  mounted() {
    this.refreshSpaceObjects()
    this.init()
    this.$emit('mounted', this)
  },

  props: {
    scenario: {
      type: Object,
      default: null
    },
    spaceObject: { type: Object, default: null },
    missionId: {
      type: String,
      default: null
    }
  },

  watch: {
    'inputs.displayForm'(newValue) {
      if (newValue) {
        this.init()
      }
    }
  },

  computed: {
    allPropertiesList() {
      return [
        ...this.getOptionalPropertiesList(this.inputs),
        ...this.getRequiredPropertiesList(this.inputs)
      ]
    },
    burnsValid() {
      return (
        this.inputs.customBurnDefinitions.length > 0 &&
        !this.inputs.customBurnDefinitions.some(
          (burn) => this.validBurns[burn.id] !== true
        )
      )
    },
    inputsValid() {
      let fieldsState = this.verifyPropertiesList(
        this.getRequiredPropertiesList(this.inputs),
        this.inputs,
        true
      )
      fieldsState =
        fieldsState &&
        this.verifyPropertiesList(
          this.getOptionalPropertiesList(this.inputs),
          this.inputs,
          false
        )
      // if impossible to validate a form : uncomment the code in verifyPropertiesList to understand the problematic field
      return this.inputNameState && fieldsState && this.burnsValid
    }
  },

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        { value: 'spaceObject', text: 'Space Object', sortable: false },
        { value: 'actions', text: 'Actions', sortable: false }
      ],

      modalRef: { content: [] },
      showModal: false,
      enableFormCache: false,

      enumPropertiesList: this.getEnumProperties(),
      spaceObjectsNotParametrics: [],
      expandedPanels: [],
      validBurns: {},

      inputs: {
        customBurnDefinitions: [],
        spaceObject: this.getDefaultValue('spaceObject'),
        missionId: null
      }
    }
  },

  methods: {
    init() {
      this.inputs.missionId = this.missionId
      if (this.scenario != null) {
        this.copyLocalFields(this.scenario)
        this.inputs.name = this.scenario.name
        this.inputs.id = this.scenario.id
        this.inputs.editing = true
      }
      if (this.spaceObject != null) {
        this.inputs.spaceObject = this.spaceObject
      }
      this.expandedPanels = this.inputs.customBurnDefinitions.map(
        (_, index) => index
      )
    },

    clearLocalFields() {
      for (let field in this.getPropertiesDescription()) {
        if (this.inputs.hasOwnProperty(field)) {
          this.inputs[field] = this.getDefaultValue(field)
        }
      }
      this.init()
    },

    getSubTypes(instance) {
      return null
    },

    getOptionalPropertiesList(instance) {
      let subTypes = this.getSubTypes(instance)
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        subTypes,
        'optional'
      )
    },

    getRequiredPropertiesList(instance) {
      let subTypes = this.getSubTypes(instance)
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        subTypes,
        'required'
      )
      return requiredList
    },

    refreshSpaceObjects() {
      let self = this
      this.$http
        .get(this.getAPI('spaceobjectdefinitions'))
        .then((response) => {
          self.spaceObjectsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of space objects. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },

    getCurrentFormType() {
      return 'CustomEvasiveScenarioDefinition'
    },

    getRefList(param) {
      if (param === 'spaceObject') {
        return this.spaceObjectsNotParametrics
      }
    },
    async updateOpenedPanelsAfterDeletion(deletedIndex) {
      this.expandedPanels = this.expandedPanels
        .filter((panelIndex) => panelIndex !== deletedIndex)
        .map((panelIndex) => {
          if (panelIndex > deletedIndex) {
            return panelIndex - 1
          } else {
            return panelIndex
          }
        })
    },
    updateBurn(indexToUpdate, newValue) {
      this.inputs.customBurnDefinitions.splice(indexToUpdate, 1, newValue)
    },
    updateBurnValid(id, isValid) {
      this.validBurns = { ...this.validBurns, [id]: isValid }
    },
    deleteBurn(indexToDelete) {
      this.inputs.customBurnDefinitions.splice(indexToDelete, 1)
      this.$nextTick().then(() =>
        this.updateOpenedPanelsAfterDeletion(indexToDelete)
      )
    },
    createNewBurn() {
      this.expandedPanels.push(this.inputs.customBurnDefinitions.length)
      this.inputs.customBurnDefinitions.push({ id: uuid() })
    }
  }
}
</script>

<style scoped>
.outlined-panel {
  border: 1px solid;
}
</style>
